import { useMemo } from 'react';
import { arrayMove, arraySwap } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { DropAnimation } from '@dnd-kit/core/dist/components/DragOverlay/hooks';

import { groupBy } from '@libs/array';

import { ParticipantDto } from '@features/shared/api/typings';

export const NO_TEAM = 'NO_TEAM';

/**
 * В этом файле лежат утилиты для работы с участниками турнира.
 * Функции для сортировки, группировки, в частности для drag-and-drop.
 */

/**
 * Вставляет участника в предыдущую команду.
 * Полезно, когда при DnD навели на команду, в которой участник уже есть (снизу вверх).
 */
export function moveParticipantToPreviousTeam(
  userId: string,
  participants: ParticipantDto[],
  maxNumberInTeam: number,
) {
  const currentParticipantIdx = participants.findIndex((p) => p.userId === userId);
  if (currentParticipantIdx === -1) return participants;
  const currentParticipant = participants[currentParticipantIdx]!;
  const oldTeamId = currentParticipant.teamId;

  // Проверяем команду на заполненность
  const targetTeamId = participants[currentParticipantIdx - 1]?.teamId || null;
  if (targetTeamId) {
    const existingParticipants = participants.filter((p) => p.teamId === targetTeamId).length;
    if (existingParticipants >= maxNumberInTeam) return participants;
  }

  currentParticipant.teamId = targetTeamId;

  return [...recalculatePositions(participants, [targetTeamId, oldTeamId])];
}

/**
 * Вставляет участника в начало команды.
 * Полезно при DnD когда навели на команду сверху вниз.
 */
export function insertParticipantIntoTeamStart(
  userId: string,
  teamId: string,
  participants: ParticipantDto[],
  maxNumberInTeam: number,
) {
  const currentParticipantIdx = participants.findIndex((p) => p.userId === userId);
  if (currentParticipantIdx === -1) return participants;
  const currentParticipant = participants[currentParticipantIdx]!;
  const oldTeamId = currentParticipant.teamId;

  // Проверяем команду на заполненность
  const existingParticipants = participants.filter((p) => p.teamId === teamId).length;
  if (existingParticipants >= maxNumberInTeam) return participants;

  currentParticipant.teamId = teamId;

  return [...recalculatePositions(participants, [oldTeamId, teamId])];
}

/**
 * Вставляет участника в конец команды.
 * Полезно, когда переносим в команду через поповер
 */
export function insertParticipantIntoTeamEnd(
  userId: string,
  teamId: string | null,
  participants: ParticipantDto[],
  maxNumberInTeam: number,
) {
  const currentParticipantIdx = participants.findIndex((p) => p.userId === userId);
  if (currentParticipantIdx === -1) return participants;
  const currentParticipant = participants[currentParticipantIdx]!;
  const oldTeamId = currentParticipant.teamId;
  const participantsInTeam = participants.filter((p) => p.teamId === teamId);
  const lastParticipantIdx = participantsInTeam.length - 1;

  // Проверяем команду на заполненность
  if (teamId) {
    const existingParticipants = participants.filter((p) => p.teamId === teamId).length;
    if (existingParticipants >= maxNumberInTeam) return participants;
  }

  currentParticipant.teamId = teamId;

  // Вставляем на место последнего участника
  let newParticipants = participants;
  if (lastParticipantIdx) {
    newParticipants = arrayMove(participants, currentParticipantIdx, lastParticipantIdx);
  }

  return [...recalculatePositions(newParticipants, [oldTeamId, teamId])];
}

/**
 * Заменяет одного участника на другого при сортировке
 */
export function moveParticipantInsteadOfAnother(
  currentUserId: string,
  targetUserId: string,
  participants: ParticipantDto[],
  maxNumberInTeam: number,
) {
  const currentParticipantIdx = participants.findIndex((p) => p.userId === currentUserId);
  const targetParticipantIdx = participants.findIndex((p) => p.userId === targetUserId);
  if (currentParticipantIdx === -1 || targetParticipantIdx === -1) return participants;

  const currentParticipant = participants[currentParticipantIdx]!;
  const currentTeamId = currentParticipant.teamId;
  const targetParticipant = participants[targetParticipantIdx]!;
  const targetTeamId = targetParticipant.teamId;

  // Проверяем команду на заполненность
  if (targetTeamId && currentTeamId !== targetTeamId) {
    const existingParticipants = participants.filter((p) => p.teamId === targetTeamId).length;
    if (existingParticipants >= maxNumberInTeam) return participants;
  }

  const newParticipants = arrayMove(participants, currentParticipantIdx, targetParticipantIdx);
  currentParticipant.teamId = targetTeamId;
  return [...recalculatePositions(newParticipants, [currentTeamId, targetTeamId])];
}

export function forcePositionInTeamForParticipant(
  currentUserId: string,
  targetNum: number,
  participants: ParticipantDto[],
  maxNumberInTeam: number,
) {
  if (targetNum > maxNumberInTeam) return participants;

  const currentParticipantIdx = participants.findIndex((p) => p.userId === currentUserId);
  const currentParticipant = participants[currentParticipantIdx]!;
  const oldNumberInTeam = currentParticipant.numberInTeam;

  const teamId = currentParticipant.teamId;

  const targetParticipantIdx = participants.findIndex(
    (p) => p.teamId === teamId && p.numberInTeam === targetNum,
  );

  currentParticipant.numberInTeam = targetNum;

  if (targetParticipantIdx === -1) {
    sortParticipantsByTeamAndPosition(participants);
    return [...participants];
  } else {
    const targetParticipant = participants[targetParticipantIdx]!;
    targetParticipant.numberInTeam = oldNumberInTeam;
    return arraySwap(participants, currentParticipantIdx, targetParticipantIdx);
  }
}

/**
 * Пересчитывает и записывает позицию участников на основе их расположения в массиве.
 * Идет по командам.
 * Можно пересчитать только для определенных команд.
 */
export const recalculatePositions = (
  participants: ParticipantDto[],
  teamIds?: (string | null)[],
) => {
  const groupedParticipants = groupParticipantsByTeams(participants)!;

  Object.keys(groupedParticipants).forEach((key) => {
    if (
      teamIds &&
      teamIds.findIndex((tId) => tId === key || (key === NO_TEAM && tId === null)) === -1
    )
      return;
    groupedParticipants[key]!.forEach((p, i) => {
      const targetField = p.teamId ? 'numberInTeam' : 'numberInTournament';
      const oppositeField = p.teamId ? 'numberInTournament' : 'numberInTeam';
      p[targetField] = i + 1;
      p[oppositeField] = null;
    });
  });

  return participants;
};

/**
 * Группирует участников по teamId или NO_TEAM, если безхозный
 */
export const groupParticipantsByTeams = (participants: ParticipantDto[]) => {
  return groupBy(participants, (p) => p.teamId || NO_TEAM);
};

export const useGroupedParticipantsByTeams = (participants?: ParticipantDto[]) => {
  return useMemo(() => {
    return groupParticipantsByTeams(participants || []);
  }, [participants]);
};

export const fixBrokenPositionsForParticipants = (participants?: ParticipantDto[]) => {
  if (!participants) return;

  participants.forEach((p) => {
    if (p.teamId === null) {
      p.numberInTeam = null;
    } else {
      p.numberInTournament = null;
    }
  });
};

/**
 * Сортирует in place в массиве на основе их numberInTeam/numberInTournament,
 * а также по порядку команд.
 */
export const sortParticipantsByTeamAndPosition = (
  participants?: ParticipantDto[],
  teamIds?: string[],
) => {
  if (!participants) return;

  const teamIdxMap: Record<string, number> = {};
  teamIds?.forEach((teamId, idx) => {
    teamIdxMap[teamId] = idx;
  });

  participants.sort((a, b) => {
    // Сначала - без команды
    if (!a.teamId && b.teamId) return -1;
    if (a.teamId && !b.teamId) return 1;

    // Если команды разные, то сортируем по командам
    if (a.teamId && b.teamId && a.teamId !== b.teamId)
      return teamIdxMap[a.teamId]! - teamIdxMap[b.teamId]!;

    // Если команды одинаковые, то сортируем по number
    const positionField = a.teamId ? 'numberInTeam' : 'numberInTournament';
    return a[positionField]! - b[positionField]!;
  });
};

/**
 * Настройки drop-анимации для dnd-kit. Без этого почему-то не отображаются элементы после сортировки :(
 */
export const DROP_ANIMATION_SETTINGS: DropAnimation = {
  duration: 250,
  easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
  keyframes({ transform }) {
    return [
      { transform: CSS.Transform.toString(transform.initial) },
      { transform: CSS.Transform.toString(transform.final) },
    ];
  },
  sideEffects: ({ active, dragOverlay }) => {
    active.node.style.opacity = '0';
    dragOverlay.node.style.opacity = '1';

    // Return cleanup function
    return () => {
      active.node.style.opacity = '';
      dragOverlay.node.style.opacity = '';
      dragOverlay.node.style.transition = '';
    };
  },
};
